import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Trash2, Pencil, Plus, Eye, EyeOff } from "lucide-react";
import { btn } from "@/components/admin/ui";
import { ProjectModal } from "@/components/admin/ProjectModal";

const ProjectRow = ({ project, catLabel, onDragStart, onDrop, onToggleHide, onEdit, onDelete }) => (
  <div
    draggable
    onDragStart={onDragStart}
    onDragOver={(e) => e.preventDefault()}
    onDrop={onDrop}
    className={`flex items-center gap-3 bg-surface border border-hairline rounded-lg px-3 py-2.5 ${project.hidden ? "opacity-50" : ""}`}
    data-testid="admin-project-row"
  >
    <GripVertical size={16} className="text-mute cursor-grab shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="font-display text-sm truncate">{project.title}</p>
      <p className="font-mono text-[10px] text-mute truncate">{catLabel} · {project.size} · {project.format}</p>
    </div>
    <button onClick={onToggleHide} className="text-mute hover:text-txt p-1" title="Hide/show">
      {project.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
    <button onClick={onEdit} className="text-mute hover:text-lav p-1" data-testid="edit-project"><Pencil size={15} /></button>
    <button onClick={onDelete} className="text-mute hover:text-red-400 p-1"><Trash2 size={15} /></button>
  </div>
);

export default function ProjectsTab({ projects, categories, reload }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [list, setList] = useState(projects);
  useEffect(() => setList(projects), [projects]);

  const catLabel = (slug) => categories.find((c) => c.slug === slug)?.label || slug;

  const onDrop = async (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const next = [...list];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setList(next);
    setDragIdx(null);
    await api.put("/projects/reorder", { ids: next.map((p) => p.id) });
    toast.success("Reordered");
  };

  const toggleHide = async (p) => {
    await api.put(`/projects/${p.id}`, { hidden: !p.hidden });
    reload();
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/projects/${p.id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-xs text-mute">{list.length} projects · drag to reorder</p>
        <button onClick={() => setAdding(true)} className={`${btn} bg-lav text-ink flex items-center gap-2`} data-testid="add-project">
          <Plus size={14} /> Add project
        </button>
      </div>

      <div className="space-y-2">
        {list.map((p, i) => (
          <ProjectRow
            key={p.id}
            project={p}
            catLabel={catLabel(p.category)}
            onDragStart={() => setDragIdx(i)}
            onDrop={() => onDrop(i)}
            onToggleHide={() => toggleHide(p)}
            onEdit={() => setEditing(p)}
            onDelete={() => del(p)}
          />
        ))}
      </div>

      {(editing || adding) && (
        <ProjectModal
          project={editing}
          categories={categories}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={reload}
        />
      )}
    </div>
  );
}
